import { Role } from '@customTypes/types';
import { MantineProvider } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { getProfiles } from '@shared/api/requests';
import { APP_CONST } from '@shared/constants';
import { useQueryClient } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { PhotoProvider } from 'react-photo-view';

import { ErrorPage, Loading, NotFoundPage } from '@/pages';
import { routeTree } from '@/routeTree.gen';
import {
  ARTIST_THEME,
  CLUB_THEME,
  DEFAULT_THEME
} from '@/shared/constants/mantine-theme';
import { useLocaleContext } from '@/shared/contexts';
import { messages } from '@/shared/i18n/messages';
// eslint-disable-next-line import/order
import { RootLayout } from '@/shared/layouts';

import 'react-photo-view/dist/react-photo-view.css';
import { getPlatformInfo } from '@/shared/utils/platformDetection';

import { LOCALES } from './shared/i18n/locales';
import { useProfileStore } from './shared/store/profileStore';
import { PushNotificationPrompt } from './shared/utils/PushNotificationPrompt';

const router = createRouter({
  routeTree,
  context: {
    auth: {
      isAuthenticated: false,
      profile: null,
      currentSessionId: null
    }
  },
  defaultErrorComponent: ErrorPage,
  defaultNotFoundComponent: NotFoundPage,
  defaultPendingComponent: Loading
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export const App = () => {
  const queryClient = useQueryClient();
  const { locale } = useLocaleContext();
  const {
    profile,
    isAuthenticated,
    currentSessionId,
    profileDataIsLoading,
    updateProfile,
    updateprofileDataIsLoading,
    updateCurrentSessionId,
    setIsSubscribed,
    setSubscriptionChecked
  } = useProfileStore();

  // Состояние для платформы
  const [isApplePlatform, setIsApplePlatform] = useState(false);
  const [isMacOS, setIsMacOS] = useState(false);

  console.log('Apple Web Push platform detected:', isApplePlatform, isMacOS);

  useEffect(() => {
    setIsApplePlatform(getPlatformInfo().isApplePlatform);
    setIsMacOS(getPlatformInfo().isMacOS);
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const authToken = localStorage.getItem(
          APP_CONST.LOCAL_STORAGE.AUTH_TOKEN
        );
        if (authToken) {
          const profileData = await queryClient.fetchQuery({
            queryKey: ['initialProfileData'],
            queryFn: () => getProfiles({})
          });
          updateProfile(profileData.data);
          updateCurrentSessionId(profileData.data.profileIds[0]);
        }
        return true;
      } catch (e) {
        updateProfile(null);
        updateCurrentSessionId(null);
        localStorage.removeItem(APP_CONST.LOCAL_STORAGE.AUTH_TOKEN);
        localStorage.removeItem(APP_CONST.LOCAL_STORAGE.REFRESH_TOKEN);
        console.error(e);
      } finally {
        updateprofileDataIsLoading(false);
      }
    };

    if (!profile) {
      checkAuth();
    }
  }, []);

  const getTheme = () => {
    if (profile == null) {
      return DEFAULT_THEME;
    }
    return profile.role === Role.artist ? ARTIST_THEME : CLUB_THEME;
  };

  // useEffect(() => {
  //   if ('serviceWorker' in navigator) {
  //     navigator.serviceWorker.register('/sw.js', { type: 'module' })
  //       .then(() => console.log('Service Worker registered'))
  //       .catch(console.error);
  //   }
  // }, []);

  useEffect(() => {
    (async () => {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        console.log('SUBSCRIPTION:', sub);
        setIsSubscribed(!!sub);
        setSubscriptionChecked(true); // <-- теперь можно показывать блок
      }
    })();
  }, []);

  return (
    <PhotoProvider>
      <IntlProvider
        defaultLocale='en'
        locale={LOCALES[locale]}
        messages={messages[LOCALES[locale]]}
      >
        <MantineProvider withCssVariables theme={getTheme()}>
          <RootLayout>
            <Notifications
              autoClose={3000}
              containerWidth={320}
              limit={2}
              position='top-center'
            />
            <PushNotificationPrompt />
            {!profileDataIsLoading ? (
              <div>
                <RouterProvider
                  router={router}
                  context={{
                    auth: {
                      isAuthenticated,
                      profile,
                      currentSessionId
                    }
                  }}
                />
              </div>
            ) : (
              <Loading />
            )}
          </RootLayout>
        </MantineProvider>
      </IntlProvider>
    </PhotoProvider>
  );
};
